import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Package, Trash2, DollarSign } from 'lucide-react';
import { db } from '../firebase/config';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { addDays } from 'date-fns';
import { EMOJIS } from '../constants/emojis';

export function EditItem() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { itemId } = useParams();
  const [name, setName] = useState('');
  const [category, setCategory] = useState<'fridge' | 'freezer' | 'pantry'>('fridge');
  const [expirationDate, setExpirationDate] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [price, setPrice] = useState('');
  const [status, setStatus] = useState<'active' | 'used' | 'wasted'>('active');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user || !itemId) {
      navigate('/dashboard');
      return;
    }

    loadItem();
  }, [user, itemId, navigate]);

  async function loadItem() {
    try {
      const itemDoc = await getDoc(doc(db, 'items', itemId!));
      
      if (!itemDoc.exists()) {
        setError('Item not found');
        setLoading(false);
        return;
      }

      const data = itemDoc.data();
      setName(data.name);
      setCategory(data.category);
      setExpirationDate(new Date(data.expirationDate).toISOString().split('T')[0]);
      setQuantity(data.quantity);
      setPrice(data.price ? data.price.toString() : '');
      setStatus(data.status || 'active');
      setLoading(false);
    } catch (err: any) {
      console.error('Error loading item:', err);
      setError('Failed to load item');
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      await updateDoc(doc(db, 'items', itemId!), {
        name,
        category,
        expirationDate: new Date(expirationDate).toISOString(),
        quantity,
        price: price ? parseFloat(price) : 0,
        status,
        updatedAt: new Date().toISOString(),
      });

      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message);
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    setSaving(true);
    try {
      await deleteDoc(doc(db, 'items', itemId!));
      navigate('/dashboard');
    } catch (err: any) {
      setError('Failed to delete item');
      setSaving(false);
    }
  }

  async function handleMarkAs(newStatus: 'used' | 'wasted') {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'items', itemId!), {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      });
      navigate('/dashboard');
    } catch (err: any) {
      setError('Failed to update status');
      setSaving(false);
    }
  }

  function setQuickDate(days: number) {
    const date = addDays(new Date(), days);
    setExpirationDate(date.toISOString().split('T')[0]);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-bg flex items-center justify-center">
        <div className="text-center">
          <img src="/logo.png" alt="Loading" className="w-20 h-20 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading item...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard"
              className="p-2 hover:bg-gray-100 rounded-lg transition-all"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </Link>
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="FridgeLife" className="w-10 h-10" />
              <h1 className="text-xl font-bold text-primary-green">Edit Item</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            {error && (
              <div className="bg-red-50 text-red-800 p-4 rounded-xl mb-6 border border-red-200">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Product Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Product Name
                </label>
                <div className="relative">
                  <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-green focus:border-primary-green transition-all"
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Storage Location
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setCategory('fridge')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      category === 'fridge'
                        ? 'border-primary-green bg-primary-mint/30'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-3xl mb-2">{EMOJIS.FRIDGE}</div>
                    <div className="text-sm font-semibold">Fridge</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCategory('freezer')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      category === 'freezer'
                        ? 'border-primary-green bg-primary-mint/30'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-3xl mb-2">{EMOJIS.FREEZER}</div>
                    <div className="text-sm font-semibold">Freezer</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCategory('pantry')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      category === 'pantry'
                        ? 'border-primary-green bg-primary-mint/30'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-3xl mb-2">{EMOJIS.PANTRY}</div>
                    <div className="text-sm font-semibold">Pantry</div>
                  </button>
                </div>
              </div>

              {/* Price and Quantity - Side by Side */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Price
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-green focus:border-primary-green transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Quantity
                  </label>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value))}
                    min={1}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-green focus:border-primary-green transition-all"
                  />
                </div>
              </div>

              {/* Expiration Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Expiration Date
                </label>

                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setQuickDate(3)}
                    className="px-4 py-2 border-2 border-gray-200 rounded-lg hover:border-primary-green hover:bg-primary-mint/20 transition-all text-sm font-medium"
                  >
                    3 days
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickDate(5)}
                    className="px-4 py-2 border-2 border-gray-200 rounded-lg hover:border-primary-green hover:bg-primary-mint/20 transition-all text-sm font-medium"
                  >
                    5 days
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickDate(7)}
                    className="px-4 py-2 border-2 border-gray-200 rounded-lg hover:border-primary-green hover:bg-primary-mint/20 transition-all text-sm font-medium"
                  >
                    1 week
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickDate(14)}
                    className="px-4 py-2 border-2 border-gray-200 rounded-lg hover:border-primary-green hover:bg-primary-mint/20 transition-all text-sm font-medium"
                  >
                    2 weeks
                  </button>
                </div>

                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-green focus:border-primary-green transition-all"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-primary-green text-white py-4 rounded-xl font-semibold hover:bg-primary-green/90 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary-green/30 transition-all hover:scale-105"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleMarkAs('used')}
                    disabled={saving}
                    className="px-4 py-3 bg-safe text-white rounded-xl font-semibold hover:bg-safe/90 disabled:opacity-50 transition-all"
                  >
                    {EMOJIS.CHECK} Mark as Used
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMarkAs('wasted')}
                    disabled={saving}
                    className="px-4 py-3 bg-warning text-white rounded-xl font-semibold hover:bg-warning/90 disabled:opacity-50 transition-all"
                  >
                    {EMOJIS.X_MARK} Mark as Wasted
                  </button>
                </div>

                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-critical text-white rounded-xl font-semibold hover:bg-critical/90 disabled:opacity-50 transition-all"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Item
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
